import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import * as XLSX from "xlsx";
import { fetchVacationCalendar } from "../../../api/TimeTracking/timeTrackingApi";
import { VACATION_TEAL, TYPE_FILL } from "./yearAbsenceCalendarUtils";
import {
  ttFilterFieldSx,
  ttKpiChipSx,
  ttTableContainerSx,
  ttToolbarButtonSx,
  ttToolbarSx,
} from "./timeTrackingStyles";
import {
  MobileEmptyState,
  MobileKpiBlock,
  MobileRecordCard,
  statusAccentFromMuiColor,
} from "./TimeTrackingMobileCards";
import TabContentShell from "./TabContentShell";
import {
  MONTH_LABELS,
  REQUEST_TYPE_LABEL,
  STATUS_LABEL,
  barStyleForRange,
  buildExportDetailRows,
  buildExportMatrixRows,
  daysInYear,
  employeeInitials,
  entriesForEmployee,
  formatShortDateRange,
  todayPositionInYear,
  totalDaysForEmployee,
} from "./vacationCalendarUtils";

const LABEL_WIDTH = 176;
const HEADER_HEIGHT = 40;
const ROW_HEIGHT = 42;
const BAR_HEIGHT = 16;

function barColorForEntry(entry) {
  return TYPE_FILL[entry.requestType] || VACATION_TEAL;
}

function LegendSwatch({ color, label, striped = false }) {
  return (
    <Stack direction="row" spacing={0.6} alignItems="center">
      <Box
        sx={{
          width: 14,
          height: 8,
          borderRadius: 4,
          flexShrink: 0,
          bgcolor: striped ? "transparent" : color,
          backgroundImage: striped
            ? `repeating-linear-gradient(135deg, ${color} 0 2px, #fff 2px 4px)`
            : "none",
          border: striped ? `1px solid ${color}` : "none",
        }}
      />
      <Typography
        sx={{ fontSize: "0.7rem", color: "text.secondary", fontWeight: 600 }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

function VacationBar({ entry, year }) {
  const style = barStyleForRange(entry.startDate, entry.endDate, year);
  if (!style) return null;

  const color = barColorForEntry(entry);
  const pending = entry.status === "pending";
  const typeLabel = REQUEST_TYPE_LABEL[entry.requestType] || entry.requestType;
  const statusLabel = STATUS_LABEL[entry.status] || entry.status;
  const rangeLabel = formatShortDateRange(entry.startDate, entry.endDate);

  const tooltip = (
    <Box sx={{ p: 0.25 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
        {typeLabel}
      </Typography>
      <Typography variant="body2">{rangeLabel}</Typography>
      <Typography variant="caption" display="block" sx={{ mt: 0.25 }}>
        {statusLabel}
        {entry.isHalfDay ? " · Medio día" : ""}
      </Typography>
      {entry.reason ? (
        <Typography
          variant="caption"
          display="block"
          sx={{ mt: 0.5, opacity: 0.9 }}
        >
          {entry.reason}
        </Typography>
      ) : null}
    </Box>
  );

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box
        sx={{
          position: "absolute",
          top: (ROW_HEIGHT - BAR_HEIGHT) / 2,
          height: BAR_HEIGHT,
          left: style.left,
          width: style.width,
          minWidth: 5,
          borderRadius: 5,
          overflow: "hidden",
          cursor: "default",
          bgcolor: pending ? "transparent" : color,
          backgroundImage: pending
            ? `repeating-linear-gradient(135deg, ${color} 0 3px, #fff 3px 6px)`
            : "none",
          boxShadow: pending ? "none" : `0 1px 2px ${alpha(color, 0.28)}`,
          zIndex: 2,
          "&:hover": { zIndex: 4, filter: "brightness(0.96)" },
        }}
      />
    </Tooltip>
  );
}

const VacationCalendarTab = ({ canRrhh }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const accent = theme.palette.primary.main;
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [requestType, setRequestType] = useState("vacation");
  const [showPending, setShowPending] = useState(true);
  const [showApproved, setShowApproved] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const statusParam = useMemo(() => {
    if (!canRrhh) return "approved";
    const parts = [];
    if (showApproved) parts.push("approved");
    if (showPending) parts.push("pending");
    return parts.length ? parts.join(",") : "approved";
  }, [canRrhh, showApproved, showPending]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchVacationCalendar({
        year,
        requestType,
        status: statusParam,
      });
      setData(result);
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo cargar el calendario");
    } finally {
      setLoading(false);
    }
  }, [year, requestType, statusParam]);

  useEffect(() => {
    load();
  }, [load]);

  const employees = data?.employees || [];
  const entries = data?.entries || [];
  const totalDays = daysInYear(year);
  const todayLeft = todayPositionInYear(year);

  const monthTicks = useMemo(() => {
    let day = 0;
    return MONTH_LABELS.map((label, idx) => {
      const daysInMonth = new Date(year, idx + 1, 0).getDate();
      const left = (day / totalDays) * 100;
      const width = (daysInMonth / totalDays) * 100;
      day += daysInMonth;
      const now = new Date();
      const isCurrent = year === now.getFullYear() && idx === now.getMonth();
      return { label, left, width, idx, isCurrent };
    });
  }, [year, totalDays]);

  const legendItems = useMemo(() => {
    const types =
      requestType === "all"
        ? [
            "vacation",
            "personal_leave",
            "sick_leave",
            "remote_day",
            "other_absence",
          ]
        : [requestType];
    const items = types.map((type) => ({
      color: TYPE_FILL[type] || VACATION_TEAL,
      label: REQUEST_TYPE_LABEL[type] || type,
      striped: false,
    }));
    if (canRrhh && showPending) {
      items.push({
        color:
          TYPE_FILL[requestType === "all" ? "vacation" : requestType] ||
          VACATION_TEAL,
        label: "Pendiente",
        striped: true,
      });
    }
    return items;
  }, [requestType, canRrhh, showPending]);

  const rowStripe = alpha(theme.palette.grey[500], 0.04);

  const exportExcel = () => {
    if (!data) return;
    const detail = buildExportDetailRows(data);
    const matrix = buildExportMatrixRows(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(matrix),
      "Calendario",
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(detail),
      "Detalle",
    );
    XLSX.writeFile(wb, `calendario_vacaciones_${year}.xlsx`);
  };

  const exportCsv = () => {
    if (!data) return;
    const matrix = buildExportMatrixRows(data);
    const ws = XLSX.utils.json_to_sheet(matrix);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `calendario_vacaciones_${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <TabContentShell accentColor={accent}>
      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Box sx={ttToolbarSx(theme)}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.25}
          sx={{ mr: 0.5 }}
        >
          <IconButton
            size="small"
            aria-label="Año anterior"
            onClick={() => setYear((y) => y - 1)}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1.05rem",
              minWidth: 48,
              textAlign: "center",
            }}
          >
            {year}
          </Typography>
          <IconButton
            size="small"
            aria-label="Año siguiente"
            onClick={() => setYear((y) => y + 1)}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>
        <FormControl
          size="small"
          sx={{ ...ttFilterFieldSx(theme), minWidth: { xs: "100%", sm: 160 } }}
        >
          <InputLabel>Tipo</InputLabel>
          <Select
            value={requestType}
            label="Tipo"
            onChange={(e) => setRequestType(e.target.value)}
          >
            <MenuItem value="vacation">Vacaciones</MenuItem>
            <MenuItem value="all">Todas las ausencias</MenuItem>
            <MenuItem value="personal_leave">Permiso personal</MenuItem>
            <MenuItem value="sick_leave">Baja / enfermedad</MenuItem>
            <MenuItem value="remote_day">Teletrabajo</MenuItem>
          </Select>
        </FormControl>
        {canRrhh ? (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showApproved}
                  onChange={(e) => setShowApproved(e.target.checked)}
                />
              }
              label="Aprobadas"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showPending}
                  onChange={(e) => setShowPending(e.target.checked)}
                />
              }
              label="Pendientes"
            />
          </Stack>
        ) : null}
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshOutlinedIcon />}
          onClick={load}
          disabled={loading}
          sx={ttToolbarButtonSx}
        >
          Actualizar
        </Button>
        <Box sx={{ flex: 1, display: { xs: "none", sm: "block" } }} />
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadOutlinedIcon />}
          onClick={exportExcel}
          disabled={!employees.length}
          sx={ttToolbarButtonSx}
        >
          Excel
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={exportCsv}
          disabled={!employees.length}
          sx={ttToolbarButtonSx}
        >
          CSV
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip
              label={`${employees.length} empleados`}
              size="small"
              sx={ttKpiChipSx}
            />
            <Chip
              label={`${entries.length} periodos`}
              size="small"
              variant="outlined"
              sx={ttKpiChipSx}
            />
          </Stack>
          {employees.map((emp) => {
            const empEntries = entriesForEmployee(entries, emp.id);
            const daysTotal = totalDaysForEmployee(empEntries, year);
            return (
              <MobileRecordCard
                key={emp.id}
                theme={theme}
                accent={
                  empEntries.length ? accent : theme.palette.text.disabled
                }
                avatarName={emp.username}
                title={emp.username}
                subtitle={
                  daysTotal ? `${daysTotal} días en ${year}` : "Sin ausencias"
                }
                statusLabel={
                  empEntries.length ? `${empEntries.length} periodo(s)` : null
                }
                statusColor="info"
              >
                {empEntries.length ? (
                  <Stack spacing={0.75}>
                    {empEntries.map((entry) => {
                      const status =
                        entry.status === "pending" ? "warning" : "success";
                      const typeLabel =
                        REQUEST_TYPE_LABEL[entry.requestType] ||
                        entry.requestType;
                      return (
                        <Box
                          key={entry.id}
                          sx={{
                            p: 1,
                            borderRadius: 1.75,
                            bgcolor: alpha(
                              statusAccentFromMuiColor(theme, status),
                              0.08,
                            ),
                            border: "1px solid",
                            borderColor: alpha(
                              statusAccentFromMuiColor(theme, status),
                              0.22,
                            ),
                          }}
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            spacing={1}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 800 }}
                              >
                                {typeLabel}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {entry.startDate} → {entry.endDate}
                                {entry.isHalfDay ? " · Medio día" : ""}
                              </Typography>
                            </Box>
                            <Chip
                              label={STATUS_LABEL[entry.status] || entry.status}
                              size="small"
                              sx={{
                                height: 22,
                                fontWeight: 700,
                                fontSize: "0.65rem",
                                flexShrink: 0,
                                bgcolor: alpha(
                                  statusAccentFromMuiColor(theme, status),
                                  0.14,
                                ),
                                color: statusAccentFromMuiColor(theme, status),
                              }}
                            />
                          </Stack>
                          {entry.reason ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5, display: "block" }}
                            >
                              {entry.reason}
                            </Typography>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <MobileKpiBlock
                    theme={theme}
                    label="Estado"
                    value="Sin registros"
                    color={theme.palette.text.disabled}
                  />
                )}
              </MobileRecordCard>
            );
          })}
          {!employees.length ? (
            <MobileEmptyState>
              No hay empleados con permiso de fichaje
            </MobileEmptyState>
          ) : null}
        </Stack>
      ) : (
        <Box
          sx={{
            ...ttTableContainerSx(theme),
            overflow: "hidden",
            bgcolor: theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              borderBottom: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.55),
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              useFlexGap
              flexWrap="wrap"
            >
              <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
                Equipo {year}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  fontWeight: 600,
                }}
              >
                {employees.length} empleados · {entries.length} periodos
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
              {legendItems.map((item) => (
                <LegendSwatch
                  key={`${item.label}-${item.striped ? "p" : "a"}`}
                  color={item.color}
                  label={item.label}
                  striped={item.striped}
                />
              ))}
            </Stack>
          </Box>

          <Box
            sx={{
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              maxHeight: { xs: "68vh", md: "72vh" },
            }}
          >
            <Box sx={{ minWidth: 960 }}>
              <Box
                sx={{
                  display: "flex",
                  position: "sticky",
                  top: 0,
                  zIndex: 4,
                  bgcolor: theme.palette.background.paper,
                  borderBottom: "1px solid",
                  borderColor: alpha(theme.palette.divider, 0.55),
                }}
              >
                <Box
                  sx={{
                    width: LABEL_WIDTH,
                    flexShrink: 0,
                    height: HEADER_HEIGHT,
                    display: "flex",
                    alignItems: "center",
                    px: 1.5,
                    position: "sticky",
                    left: 0,
                    zIndex: 5,
                    bgcolor: theme.palette.background.paper,
                    borderRight: "1px solid",
                    borderColor: alpha(theme.palette.divider, 0.55),
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 800,
                      letterSpacing: 0.6,
                      textTransform: "uppercase",
                      color: "text.secondary",
                      fontSize: "0.65rem",
                    }}
                  >
                    Empleado
                  </Typography>
                </Box>
                <Box
                  sx={{ flex: 1, position: "relative", height: HEADER_HEIGHT }}
                >
                  {monthTicks.map((m) => (
                    <Tooltip key={m.label} title={`${m.label} ${year}`} arrow>
                      <Box
                        sx={{
                          position: "absolute",
                          left: `${m.left}%`,
                          width: `${m.width}%`,
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderLeft: "1px solid",
                          borderColor: alpha(theme.palette.divider, 0.35),
                          bgcolor: m.isCurrent
                            ? alpha(VACATION_TEAL, 0.08)
                            : m.idx % 2 === 0
                              ? alpha(theme.palette.grey[500], 0.03)
                              : "transparent",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: m.isCurrent ? 800 : 700,
                            fontSize: "0.7rem",
                            letterSpacing: 0.2,
                            color: m.isCurrent
                              ? VACATION_TEAL
                              : "text.secondary",
                          }}
                        >
                          {m.label}
                        </Typography>
                      </Box>
                    </Tooltip>
                  ))}
                  {todayLeft != null ? (
                    <Box
                      sx={{
                        position: "absolute",
                        left: `${todayLeft}%`,
                        top: 0,
                        bottom: 0,
                        width: 0,
                        borderLeft: `2px solid ${theme.palette.primary.main}`,
                        zIndex: 3,
                        pointerEvents: "none",
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 5,
                          left: 5,
                          px: 0.55,
                          py: 0.1,
                          borderRadius: 1,
                          bgcolor: theme.palette.primary.main,
                          color: "#fff",
                          fontSize: "0.58rem",
                          fontWeight: 800,
                          lineHeight: 1.35,
                          letterSpacing: 0.2,
                        }}
                      >
                        Hoy
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              </Box>

              {employees.map((emp, rowIdx) => {
                const empEntries = entriesForEmployee(entries, emp.id);
                const daysTotal = totalDaysForEmployee(empEntries, year);
                const hasEntries = empEntries.length > 0;
                const stripe = rowIdx % 2 === 1;

                return (
                  <Box
                    key={emp.id}
                    sx={{
                      display: "flex",
                      minHeight: ROW_HEIGHT,
                      bgcolor: stripe ? rowStripe : "transparent",
                      borderBottom: "1px solid",
                      borderColor: alpha(theme.palette.divider, 0.22),
                      "&:hover": {
                        bgcolor: alpha(VACATION_TEAL, 0.05),
                        "& .vc-employee-cell": {
                          bgcolor: alpha(VACATION_TEAL, 0.05),
                        },
                      },
                    }}
                  >
                    <Box
                      className="vc-employee-cell"
                      sx={{
                        width: LABEL_WIDTH,
                        flexShrink: 0,
                        px: 1.25,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                        bgcolor: stripe
                          ? alpha(theme.palette.grey[500], 0.04)
                          : theme.palette.background.paper,
                        borderRight: "1px solid",
                        borderColor: alpha(theme.palette.divider, 0.55),
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 800,
                          fontSize: "0.65rem",
                          color: hasEntries ? VACATION_TEAL : "text.secondary",
                          bgcolor: hasEntries
                            ? alpha(VACATION_TEAL, 0.12)
                            : alpha(theme.palette.text.primary, 0.05),
                        }}
                      >
                        {employeeInitials(emp.username)}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            lineHeight: 1.2,
                            fontSize: "0.82rem",
                          }}
                          noWrap
                          title={emp.username}
                        >
                          {emp.username}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: daysTotal
                              ? "text.secondary"
                              : "text.disabled",
                            fontWeight: daysTotal ? 600 : 400,
                            fontSize: "0.65rem",
                          }}
                        >
                          {daysTotal ? `${daysTotal} días` : "Sin ausencias"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        position: "relative",
                        minHeight: ROW_HEIGHT,
                      }}
                    >
                      {monthTicks.map((m) => (
                        <Box
                          key={`${emp.id}-${m.label}`}
                          sx={{
                            position: "absolute",
                            left: `${m.left}%`,
                            width: `${m.width}%`,
                            top: 0,
                            bottom: 0,
                            borderLeft: "1px solid",
                            borderColor: alpha(theme.palette.divider, 0.2),
                            bgcolor: m.isCurrent
                              ? alpha(VACATION_TEAL, 0.04)
                              : "transparent",
                            pointerEvents: "none",
                          }}
                        />
                      ))}

                      {todayLeft != null ? (
                        <Box
                          sx={{
                            position: "absolute",
                            left: `${todayLeft}%`,
                            top: 0,
                            bottom: 0,
                            width: 0,
                            borderLeft: `1px solid ${alpha(theme.palette.primary.main, 0.55)}`,
                            zIndex: 1,
                            pointerEvents: "none",
                          }}
                        />
                      ) : null}

                      {empEntries.map((entry) => (
                        <VacationBar key={entry.id} entry={entry} year={year} />
                      ))}
                    </Box>
                  </Box>
                );
              })}

              {!employees.length ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography color="text.secondary" sx={{ fontWeight: 600 }}>
                    No hay empleados con permiso de fichaje
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>
      )}
    </TabContentShell>
  );
};

export default VacationCalendarTab;
